import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  IconButton,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  RestoreFromTrash as TrashIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  AccountTree as TreeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import PlanificationTypeService from '../../services/planificationType.service';

const PlanificationTypeList = () => {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState({});
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    setLoading(true);
    try {
      const res = await PlanificationTypeService.getAll();
      setTypes(res.data?.data || []);
    } catch (e) {
      console.error('Error loading planification types:', e);
      showSnackbar(t('planifications.errorLoadingPlanificationTypes'), 'error');
      setTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredTypes = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return types;
    return types.filter(t => (t.name || '').toLowerCase().includes(q));
  }, [types, searchTerm]);

  const tree = useMemo(() => {
    const byId = new Map();
    filteredTypes.forEach(t => byId.set(t.id, { ...t, children: [] }));

    const roots = [];
    byId.forEach(node => {
      if (node.parent_id && byId.has(node.parent_id)) {
        byId.get(node.parent_id).children.push(node);
      } else {
        roots.push(node);
      }
    });

    const sortRec = (nodes) => {
      nodes.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      nodes.forEach(n => sortRec(n.children));
    };
    sortRec(roots);

    return roots;
  }, [filteredTypes]);

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleDelete = (item) => {
    setDeleteDialog({ open: true, item });
  };

  const confirmDelete = async () => {
    const item = deleteDialog.item;
    if (!item) return;

    try {
      await PlanificationTypeService.delete(item.id);
      showSnackbar('Planification type deleted successfully');
      fetchTypes();
    } catch (e) {
      console.error('Error deleting planification type:', e);
      showSnackbar(t('planifications.errorDeletingPlanificationType'), 'error');
    } finally {
      setDeleteDialog({ open: false, item: null });
    }
  };

  const renderNode = (node, level = 0) => {
    const hasChildren = (node.children || []).length > 0;
    const isOpen = !!expanded[node.id];

    return (
      <Box key={node.id} sx={{ pl: level * 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 0.75,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            {hasChildren ? (
              <IconButton size="small" onClick={() => toggleExpand(node.id)}>
                {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            ) : (
              <Box sx={{ width: 40 }} />
            )}
            <Typography variant="body1" sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {node.name}
            </Typography>
            {node.parent_id ? <Chip label={t('planifications.child')} size="small" variant="outlined" /> : <Chip label={t('planifications.root')} size="small" variant="outlined" />}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {hasPermission('planification-types.create') && (
              <IconButton
                size="small"
                color="primary"
                title={t('planifications.addChildType')}
                onClick={() => navigate(`/planifications/types/new?parent_id=${node.id}`)}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            )}
            {hasPermission('planification-types.edit') && (
              <IconButton
                size="small"
                color="primary"
                title={t('common.edit')}
                onClick={() => navigate(`/planifications/types/${node.id}/edit`)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            {hasPermission('planification-types.delete') && (
              <IconButton
                size="small"
                color="error"
                title={t('common.delete')}
                onClick={() => handleDelete(node)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        {hasChildren && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Box>
              {node.children.map(child => renderNode(child, level + 1))}
            </Box>
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TreeIcon />
              <Typography variant="h5">{t('planifications.typesTitle')}</Typography>
            </Box>
          }
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              {hasPermission('planification-types.view') && (
                <Button
                  variant="outlined"
                  startIcon={<TrashIcon />}
                  onClick={() => navigate('/planifications/types/deleted')}
                >
                  {t('planifications.deleted')}
                </Button>
              )}
              {hasPermission('planification-types.create') && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/planifications/types/new')}
                >
                  {t('planifications.newType')}
                </Button>
              )}
            </Box>
          }
        />
        <CardContent>
          <TextField
            placeholder={t('planifications.searchTypes')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ mb: 2, minWidth: 320 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : tree.length === 0 ? (
            <Box py={3}>
              <Typography color="text.secondary">{t('planifications.noTypesFound')}</Typography>
            </Box>
          ) : (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              {tree.map(root => renderNode(root, 0))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, item: null })}>
        <DialogTitle>{t('planifications.deleteTypeTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('planifications.deleteTypeConfirm', { name: deleteDialog.item?.name || '' })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('planifications.deleteTypeConfirmSoft')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, item: null })}>{t('common.cancel')}</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlanificationTypeList;
